import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Fade,
  Grid,
  Grow,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';

const floatIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const lineSweep = keyframes`
  from {
    transform: translateX(-35%);
  }
  to {
    transform: translateX(35%);
  }
`;

const darkGridSx = {
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: '#202429',
  color: '#fff',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    opacity: 0.12,
    backgroundImage:
      'linear-gradient(rgba(255,255,255,0.9) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.9) 1px, transparent 1px)',
    backgroundSize: '42px 42px',
    pointerEvents: 'none',
  },
};

const benefits = [
  {
    icon: <VerifiedRoundedIcon sx={{ fontSize: 30 }} />,
    title: 'Empresas verificadas',
    description: 'Encontre empresas da comunidade IDEBRASIL com mais segurança e confiança.',
  },
  {
    icon: <SearchIcon sx={{ fontSize: 30 }} />,
    title: 'Busca inteligente',
    description: 'Pesquise fornecedores, serviços e parceiros por segmento, cidade ou atividade.',
  },
  {
    icon: <InsightsRoundedIcon sx={{ fontSize: 30 }} />,
    title: 'Mais oportunidades',
    description: 'Quanto mais empresários utilizam o IDECONECTA, mais negócios surgem para toda a comunidade.',
  },
];

const steps = [
  {
    step: '01',
    title: 'Cadastre sua empresa',
    description: 'Preencha seu perfil e torne sua empresa visível para milhares de empresários.',
  },
  {
    step: '02',
    title: 'Seja encontrado',
    description: 'Empresários da comunidade poderão encontrar sua empresa e solicitar orçamentos.',
  },
  {
    step: '03',
    title: 'Gere novos negócios',
    description:
      'Sempre que precisar de um produto, serviço ou fornecedor, procure primeiro no IDECONECTA. Quanto mais empresários utilizam a plataforma, mais oportunidades surgem para todos.',
  },
];

const Home: React.FC = () => {
  return (
    <Box sx={{ backgroundColor: '#f4f6f8' }}>
      <Box
        sx={{
          ...darkGridSx,
          py: { xs: 7, md: 10 },
          borderBottom: '6px solid #c23535',
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 2,
            bgcolor: '#2f8f4e',
            animation: `${lineSweep} 5s ease-in-out infinite alternate`,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Fade in timeout={700}>
            <Stack spacing={3} alignItems="center" textAlign="center" sx={{ maxWidth: 900, mx: 'auto' }}>
              <Box
                component="img"
                src="/logo-white.png?v=20260611"
                alt="IDECONECTA"
                sx={{
                  width: { xs: 220, sm: 280, md: 330 },
                  maxWidth: '76vw',
                  height: 'auto',
                  objectFit: 'contain',
                  animation: `${floatIn} 700ms ease both`,
                }}
              />

              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: '2.25rem', sm: '3rem', md: '4rem' },
                  lineHeight: 1.08,
                  fontWeight: 700,
                  letterSpacing: 0,
                  color: '#ffffff',
                  textShadow: '0 18px 42px rgba(0,0,0,0.42)',
                  animation: `${floatIn} 800ms ease 80ms both`,
                }}
              >
                O lugar onde Gestores e Gestoras de Impacto fazem negócios entre si.
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: '1.05rem', md: '1.18rem' },
                  maxWidth: 760,
                  color: 'rgba(255,255,255,0.94)',
                  textShadow: '0 10px 28px rgba(0,0,0,0.32)',
                  animation: `${floatIn} 800ms ease 160ms both`,
                }}
              >
                Antes de buscar no mercado, procure primeiro no IDECONECTA. Encontre fornecedores,
                parceiros e oportunidades dentro da comunidade IDEBRASIL.
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="center"
                sx={{ animation: `${floatIn} 800ms ease 240ms both` }}
              >
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/busca"
                  startIcon={<PlayArrowRoundedIcon />}
                  sx={{
                    minWidth: 220,
                    bgcolor: '#2f8f4e',
                    color: '#fff',
                    fontWeight: 700,
                    borderRadius: 1,
                    py: 1.4,
                    boxShadow: '0 16px 34px rgba(47,143,78,0.24)',
                    transition: 'transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
                    '&:hover': {
                      bgcolor: '#267342',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 20px 40px rgba(47,143,78,0.32)',
                    },
                  }}
                >
                  Encontrar fornecedores
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/empresa/cadastro"
                  startIcon={<RocketLaunchRoundedIcon />}
                  sx={{
                    minWidth: 220,
                    bgcolor: '#2f8f4e',
                    color: '#fff',
                    fontWeight: 700,
                    borderRadius: 1,
                    py: 1.4,
                    boxShadow: '0 16px 34px rgba(47,143,78,0.24)',
                    transition: 'transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
                    '&:hover': {
                      bgcolor: '#267342',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 20px 40px rgba(47,143,78,0.32)',
                    },
                  }}
                >
                  Cadastrar minha empresa
                </Button>
              </Stack>
            </Stack>
          </Fade>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 7, md: 9 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={5}>
              <Stack
                spacing={2}
                sx={{
                  height: '100%',
                  justifyContent: 'center',
                  p: { xs: 0, md: 3 },
                  animation: `${floatIn} 700ms ease both`,
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.15, color: '#202429' }}>
                  Uma plataforma criada para gerar negócios entre empresários
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '1.02rem' }}>
                  O IDECONECTA conecta empresas da comunidade IDEBRASIL para comprar, vender, contratar
                  serviços e criar novas oportunidades de negócio.
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} md={7}>
              <Grid container spacing={2.5}>
                {benefits.map((item, index) => (
                  <Grid item xs={12} md={4} key={item.title}>
                    <Grow in timeout={500 + index * 120}>
                      <Card
                        elevation={0}
                        sx={{
                          height: '100%',
                          borderRadius: 1,
                          border: '1px solid rgba(31,35,40,0.08)',
                          bgcolor: '#ffffff',
                          boxShadow: '0 14px 34px rgba(31,35,40,0.08)',
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            height: 4,
                            width: '100%',
                            bgcolor: index === 0 ? '#c23535' : index === 1 ? '#2f8f4e' : '#202429',
                            transform: 'scaleX(0.25)',
                            transformOrigin: 'left',
                            transition: 'transform 220ms ease',
                          },
                          '&:hover': {
                            transform: 'translateY(-7px)',
                            borderColor: 'rgba(47,143,78,0.42)',
                            boxShadow: '0 24px 52px rgba(31,35,40,0.14)',
                          },
                          '&:hover::before': {
                            transform: 'scaleX(1)',
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2.5 }}>
                          <Box
                            sx={{
                              width: 46,
                              height: 46,
                              borderRadius: 1,
                              bgcolor: 'rgba(47,143,78,0.10)',
                              color: '#2f8f4e',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 2,
                            }}
                          >
                            {item.icon}
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#202429' }}>
                            {item.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grow>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 7, md: 9 }, bgcolor: '#f4f6f8' }}>
        <Container maxWidth="lg">
          <Stack spacing={1.5} sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#202429' }}>
              Como funciona o IDECONECTA
            </Typography>
          </Stack>

          <Grid container spacing={3}>
            {steps.map((item) => (
              <Grid item xs={12} md={4} key={item.step}>
                <Paper
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 1,
                    p: 3,
                    bgcolor: '#fff',
                    border: '1px solid rgba(31,35,40,0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 14px 34px rgba(31,35,40,0.06)',
                    transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      right: -32,
                      top: -32,
                      width: 92,
                      height: 92,
                      borderRadius: '50%',
                      bgcolor: 'rgba(194,53,53,0.07)',
                      transition: 'transform 220ms ease',
                    },
                    '&:hover': {
                      transform: 'translateY(-7px)',
                      borderColor: 'rgba(194,53,53,0.35)',
                      boxShadow: '0 24px 52px rgba(31,35,40,0.12)',
                    },
                    '&:hover::after': {
                      transform: 'scale(1.25)',
                    },
                  }}
                >
                  <Typography sx={{ color: '#c23535', fontWeight: 700, mb: 1, position: 'relative', zIndex: 1 }}>
                    {item.step}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#202429' }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box
        sx={{
          ...darkGridSx,
          py: { xs: 7, md: 9 },
          borderTop: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Stack spacing={3} alignItems={{ xs: 'flex-start', md: 'center' }} textAlign={{ xs: 'left', md: 'center' }}>
            <Typography variant="h3" sx={{ fontWeight: 700, maxWidth: 900, lineHeight: 1.14, color: '#ffffff' }}>
              Antes de procurar no mercado, procure primeiro no IDECONECTA.
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.78)', maxWidth: 760, fontSize: '1.05rem' }}>
              Junte-se a centenas de empreendedores já estão fazendo negócios dentro da comunidade do
              IDEBRASIL.
            </Typography>
            <Typography sx={{ fontWeight: 700, color: '#fff' }}>
              Uma comunidade de empresários que compram, vendem e crescem juntos.
            </Typography>
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/empresa/cadastro"
              sx={{
                bgcolor: '#2f8f4e',
                fontWeight: 700,
                borderRadius: 1,
                py: 1.4,
                px: 3,
                boxShadow: '0 16px 34px rgba(47,143,78,0.24)',
                transition: 'transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
                '&:hover': {
                  bgcolor: '#267342',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 20px 40px rgba(47,143,78,0.32)',
                },
              }}
            >
              Cadastrar minha empresa
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
